import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Accordion, Anchor, Badge, Group, Loader, Modal, SegmentedControl, Stack, Tabs, Text, Timeline } from "@mantine/core";
import { IconBell, IconBrandWhatsapp, IconCalendar,IconCheck, IconEye, IconMail, IconMessageCircle, IconSend, IconX } from "@tabler/icons-react";

import { formatDateToString } from "@utils/dateValidation";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import { useGetAppointmentCalendarEvents } from "@hooks/queries/appointment/useGetAppointmentCalendarEvents";
import { useGetAppointmentNotifications } from "@hooks/queries/appointment/useGetAppointmentNotifications";

import { StyledNotificationHistoryModal } from "./NotificationHistoryModal.styled";


interface NotificationHistoryModalProps {
  opened: boolean;
  close: () => void;
  appointmentId: string;
}

export const NotificationHistoryModal = ({
  opened,
  close,
  appointmentId,
}: NotificationHistoryModalProps) => {
  const { t } = useTranslation();
  const [notificationFilter, setNotificationFilter] = useState("all");

  const { data, isLoading, isError, isSuccess } = useGetAppointmentNotifications(
    appointmentId,
    opened
  );

  const { 
    data: calendarEvent, 
    isLoading: isCalendarLoading, 
    isError: isCalendarError, 
    isSuccess: isCalendarSuccess 
  } = useGetAppointmentCalendarEvents(appointmentId, opened);
  const filteredNotifications = (data || []).filter((notification: any) => {
    if (notificationFilter === "chat") return notification.notification_type === "chat_new_message";
    if (notificationFilter === "appointment") return notification.notification_type !== "chat_new_message";
    return true;
  });

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case "email": return <IconMail size={14} />;
      case "whatsapp": return <IconBrandWhatsapp size={14} />;
      case "sms": return <IconMessageCircle size={14} />;
      case "push": return <IconBell size={14} />;
      default: return <IconBell size={14} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "delivered": return "green";
      case "read": return "blue";
      case "sent": return "cyan";
      case "failed": return "red";
      case "queued": return "yellow";
      default: return "gray";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "delivered": return <IconCheck size={12} />;
      case "read": return <IconEye size={12} />;
      case "sent": return <IconSend size={12} />;
      case "failed": return <IconX size={12} />;
      default: return null;
    }
  };

  const getRecipientPhoneNumber = (notification: any, recipientType: string) => {
    if (recipientType === 'patient') {
      return notification.data?.patient_recipient_phone;
    } else if (recipientType === 'medical_professional') {
      return notification.data?.prof_recipient_phone;
    }
    return null;
  };

  const getRecipientEmail = (notification: any, recipientType: string) => {
    if (recipientType === 'medical_professional') {
      return notification.data?.prof_recipient_email || notification.data?.doctor_email;
    }
    return null;
  };

  const getChatRecipientLabel = (recipientType: string) => {
    if (recipientType === "patient") return t("chat-recipient-patient");
    if (recipientType === "medical_professional") return t("chat-recipient-doctor");
    return recipientType;
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={t("notification-history")}
      size="lg"
      centered
    >
      <StyledNotificationHistoryModal>
        <Tabs defaultValue="notifications">
          <Tabs.List>
            <Tabs.Tab value="notifications" leftSection={<IconBell size={14} />}>
              {t("notifications")}
            </Tabs.Tab>
            <Tabs.Tab value="calendar" leftSection={<IconCalendar size={14} />}>
              {t("calendar")}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="notifications" pt="xs">
            <SegmentedControl
              fullWidth
              mb="md"
              value={notificationFilter}
              onChange={setNotificationFilter}
              data={[
                { value: "all", label: t("notification-filter-all") },
                { value: "appointment", label: t("notification-filter-appointments") },
                { value: "chat", label: t("notification-filter-chat") },
              ]}
            />
            {renderLoadingErrorData({
              isLoading,
              isError,
              isSuccess,
              dataLength: filteredNotifications.length,
              loadingComponent: <Loader size="sm" />,
              errorComponent: <Text c="red">{t("error-loading-notifications")}</Text>,
              emptyDataComponent: <Text c="dimmed" ta="center">{t(notificationFilter === "chat" ? "no-chat-notifications-found" : "no-notifications-found")}</Text>,
              dataComponent: (
                <Timeline active={filteredNotifications.length} bulletSize={24} lineWidth={2}>
                  {filteredNotifications.map((notification: any) => (
                    <Timeline.Item 
                      key={notification._id} 
                      title={notification.notification_type === "chat_new_message" ? t("chat-push-notification") : t(notification.notification_type)}
                      bullet={<IconBell size={12} />}
                    >
                      <Text c="dimmed" size="sm">
                        {formatDateToString(new Date(notification.created_at), true)}
                      </Text>
                      {notification.notification_type === "chat_new_message" && (
                        <Text size="sm">
                          {t("chat-notification-from", { sender: notification.data?.sender_name || t("unknown-sender") })}
                        </Text>
                      )}
                      
                      <Accordion variant="separated" mt="sm">
                        {notification.recipients.map((recipient: any) => {
                          const phoneNumber = getRecipientPhoneNumber(notification, recipient.recipient_type);
                          const recipientEmail = getRecipientEmail(notification, recipient.recipient_type);
                          
                          return (
                            <Accordion.Item key={recipient.recipient_id} value={recipient.recipient_id}>
                              <Accordion.Control>
                                <Group justify="space-between">
                                  <div>
                                    <Text size="sm" fw={500}>
                                      {notification.notification_type === "chat_new_message"
                                        ? t("chat-notification-to", {
                                            recipient: getChatRecipientLabel(recipient.recipient_type),
                                          })
                                        : recipient.recipient_type} ({recipient.channels.join(", ")})
                                    </Text>
                                    {notification.notification_type === "chat_new_message" && recipient.recipient_id && (
                                      <Text size="xs" c="dimmed">{t("chat-recipient-id", { id: recipient.recipient_id })}</Text>
                                    )}
                                    {phoneNumber && (
                                      <Text size="xs" c="dimmed">
                                        {phoneNumber}
                                      </Text>
                                    )}
                                    {recipientEmail && (
                                      <Text size="xs" c="dimmed">
                                        {recipientEmail}
                                      </Text>
                                    )}
                                  </div>
                                  <Badge color={getStatusColor(recipient.status)}>
                                    {recipient.status}
                                  </Badge>
                                </Group>
                              </Accordion.Control>
                              <Accordion.Panel>
                                <Stack gap="xs">
                                  {recipient.delivery_attempts?.map((attempt: any, index: number) => (
                                    <Group key={index} justify="space-between" wrap="nowrap">
                                      <Group gap="xs">
                                        {getChannelIcon(attempt.channel)}
                                        <Text size="xs" tt="uppercase" fw={700}>{attempt.channel}</Text>
                                      </Group>
                                      <Group gap="xs">
                                        <Badge 
                                          size="sm" 
                                          variant="light" 
                                          color={getStatusColor(attempt.status)}
                                          leftSection={getStatusIcon(attempt.status)}
                                        >
                                          {attempt.status}
                                        </Badge>
                                        <Text size="xs" c="dimmed">
                                          {formatDateToString(new Date(attempt.attempt_time), true)}
                                        </Text>
                                      </Group>
                                      {attempt.error_message && (
                                        <Text size="xs" c="red" style={{ width: '100%' }}>
                                          Error: {attempt.error_message}
                                        </Text>
                                      )}
                                    </Group>
                                  ))}
                                </Stack>
                              </Accordion.Panel>
                            </Accordion.Item>
                          );
                        })}
                      </Accordion>
                    </Timeline.Item>
                  ))}
                </Timeline>
              )
            })}
          </Tabs.Panel>

          <Tabs.Panel value="calendar" pt="xs">
            {renderLoadingErrorData({
              isLoading: isCalendarLoading,
              isError: isCalendarError,
              isSuccess: isCalendarSuccess,
              dataLength: calendarEvent ? 1 : 0,
              loadingComponent: <Loader size="sm" />,
              errorComponent: <Text c="red">{t("error-loading-calendar-events")}</Text>,
              emptyDataComponent: <Text c="dimmed" ta="center">{t("no-calendar-invite-generated")}</Text>,
              dataComponent: (
                <Stack>
                  <Group justify="space-between">
                    <Text size="lg" fw={500}>Calendar Invite Details</Text>
                    <Badge color={calendarEvent?.is_cancelled ? "red" : "green"}>
                      {calendarEvent?.is_cancelled ? "Cancelled" : "Active"}
                    </Badge>
                  </Group>

                  {calendarEvent?.doctor_email && (
                    <Group>
                      <Text fw={500}>Doctor Email:</Text>
                      <Text size="sm">{calendarEvent.doctor_email}</Text>
                    </Group>
                  )}

                  <Group>
                    <Text fw={500}>Delivery Method:</Text>
                    <Badge variant="outline">{calendarEvent?.delivery_method}</Badge>
                  </Group>

                  {calendarEvent?.google_event_id && (
                    <Group>
                      <Text fw={500}>Google Event ID:</Text>
                      <Text size="sm">{calendarEvent.google_event_id}</Text>
                    </Group>
                  )}

                  {calendarEvent?.google_event_url && (
                    <Group>
                      <Text fw={500}>Event Link:</Text>
                      <Anchor href={calendarEvent.google_event_url} target="_blank" rel="noopener noreferrer">
                        View in Google Calendar
                      </Anchor>
                    </Group>
                  )}

                  {calendarEvent?.ics_filename && (
                    <Group>
                      <Text fw={500}>ICS File:</Text>
                      <Text size="sm">{calendarEvent.ics_filename}</Text>
                    </Group>
                  )}

                  <Group>
                    <Text fw={500}>Sequence Number:</Text>
                    <Text size="sm">{calendarEvent?.sequence_number}</Text>
                  </Group>

                  <Group>
                    <Text fw={500}>Last Updated:</Text>
                    <Text size="sm">
                      {calendarEvent?.updated_at && formatDateToString(new Date(calendarEvent.updated_at), true)}
                    </Text>
                  </Group>
                  
                  <Group>
                    <Text fw={500}>Created At:</Text>
                    <Text size="sm">
                      {calendarEvent?.created_at && formatDateToString(new Date(calendarEvent.created_at), true)}
                    </Text>
                  </Group>
                </Stack>
              )
            })}
          </Tabs.Panel>
        </Tabs>
      </StyledNotificationHistoryModal>
    </Modal>
  );
};
